import { collection, getDocs, getDoc, addDoc, doc, updateDoc, deleteDoc, setDoc, query, where, orderBy, limit, writeBatch, serverTimestamp, Timestamp, onSnapshot, increment } from 'firebase/firestore';
import { ref, uploadBytesResumable } from 'firebase/storage';
import { auth, storage, db } from '../firebase';

// ── Variant display helper ────────────────────────────────────────────────────
// Converts controlled-vocab values to readable labels:
// 'extra_mature' → 'Extra Mature', 'full_fat' → 'Full Fat', etc.
export function formatVariant(variant) {
  if (!variant) return null;
  // Handle percentage variants before generic split
  const VARIANT_LABELS = {
    '0_percent': '0%',
    '5_percent': '5%',
  };
  if (VARIANT_LABELS[variant]) return VARIANT_LABELS[variant];
  return variant
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

// Helper: Convert pence to pounds for display
const penceToPounds = (pence) => {
    return (pence / 100).toFixed(2);
};

// Helper: Convert pounds to pence for storage
const poundsToPence = (pounds) => {
    return Math.round(pounds * 100);
};

// Get all stores
export const getStores = async () => {
    try {
        const storesRef = collection(db, 'stores');
        const snapshot = await getDocs(storesRef);
        return snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
    } catch (error) {
        console.error('Error fetching stores:', error);
        return [];
    }
};

// Search products by name (with prices from nested structure)
export const searchProducts = async (searchTerm) => {
    try {
        console.log('=== SEARCH PRODUCTS (NEW STRUCTURE) ===');
        console.log('Search term:', searchTerm);
        
        // Get all products
        const productsRef = collection(db, 'products');
        const productsSnapshot = await getDocs(productsRef);
        
        console.log('Products found:', productsSnapshot.size);

        const allProducts = productsSnapshot.docs.map(doc => ({ 
            id: doc.id, 
            ...doc.data() 
        }));

        // Filter by search term with smart matching
        let filtered = allProducts;
        if (searchTerm && searchTerm.trim()) {
            filtered = allProducts.filter(p =>
                smartMatch(p.name, searchTerm) ||
                smartMatch(p.category, searchTerm) ||
                smartMatch(p.brand, searchTerm)
            );
        }
        
        console.log('Filtered products:', filtered.length);

        // Fetch prices from nested structure
        const productsWithPrices = await Promise.all(
            filtered.map(async (product) => {
                try {
                    // Get all retailers for this product
                    const retailersRef = collection(db, 'products', product.id, 'retailers');
                    const retailersSnapshot = await getDocs(retailersRef);
                    
                    // Build prices array from latest prices
                    const prices = retailersSnapshot.docs.map(retailerDoc => {
                        const data = retailerDoc.data();
                        return {
                            id: retailerDoc.id,
                            storeId: data.retailerId,
                            price: data.latestPricePence / 100, // Convert pence to pounds for display
                            pricePence: data.latestPricePence,
                            inStock: data.inStock,
                            updatedAt: data.latestTs,
                            // Brand — populated for branded rows (e.g. 'Heinz'), null for own brand
                            brand: data.brand || null,
                            // Store's own product title — used in mixed-family row display
                            productNameStore: data.productNameStore || null,
                            // Per-store pack size — fall back to the product's standardizedSize
                            // when storeSize (col 8) is not populated in the sheet. This ensures
                            // PriceComparison can build pack-size chips even before the sheet
                            // has store-specific sizes filled in.
                            size: data.size || product.standardizedSize || null,
                            standardizedSize: data.standardizedSize || product.standardizedSize || null,
                            unitType: data.unitType || product.unitType || null,
                        };
                    });
                    
                    return { ...product, prices };
                } catch (error) {
                    console.error(`Error fetching prices for ${product.id}:`, error);
                    return { ...product, prices: [] };
                }
            })
        );
        
        console.log('Products with prices loaded:', productsWithPrices.length);
        return productsWithPrices;
    } catch (error) {
        console.error('Error searching products:', error);
        return [];
    }
};

// Smart search helper - handles variations, typos, partial matches
const smartMatch = (productText, searchTerm) => {
    if (!productText || !searchTerm) return false;
    
    const product = productText.toLowerCase();
    const search = searchTerm.toLowerCase().trim();
    
    // Direct match
    if (product.includes(search)) return true;
    
    // Common spelling variations
    const variations = {
        'beans': ['beanz'],
        'beanz': ['beans'],
        'ketchup': ['catsup', 'catchup'],
        'yogurt': ['yoghurt', 'yoghourt'],
        'mayonnaise': ['mayo'],
        'mayo': ['mayonnaise'],
        'semi': ['semi-skimmed', 'semiskimmed'],
        'skimmed': ['skim'],
        'whole': ['whole milk'],
        'milk': ['mlk'],
        'mlk': ['milk'],
        'chips': ['fries'],
        'pg': ['pg tips'],
        'heinz': ['hienz']
    };
    
    // Check if search term has variations
    if (variations[search]) {
        for (const variant of variations[search]) {
            if (product.includes(variant)) return true;
        }
    }
    
    // Word boundary matching - only match whole words for short searches
    if (search.length <= 4) {
        const regex = new RegExp(`\\b${search}\\b`, 'i');
        return regex.test(product);
    }
    
    return false;
};

// Get products by category (with prices)
export const getProductsByCategory = async (category) => {
    try {
        const productsRef = collection(db, 'products');
        const snapshot = await getDocs(productsRef);

        const products = snapshot.docs
            .map(doc => ({
                id: doc.id,
                ...doc.data()
            }))
            .filter(p => p.category?.toLowerCase() === category.toLowerCase());

        // Fetch prices for each product
        const productsWithPrices = await Promise.all(
            products.map(async (product) => {
                try {
                    const retailersRef = collection(db, 'products', product.id, 'retailers');
                    const retailersSnapshot = await getDocs(retailersRef);
                    
                    const prices = retailersSnapshot.docs.map(retailerDoc => {
                        const data = retailerDoc.data();
                        return {
                            id: retailerDoc.id,
                            storeId: data.retailerId,
                            price: data.latestPricePence / 100,
                            pricePence: data.latestPricePence,
                            inStock: data.inStock,
                            brand: data.brand || null,
                            productNameStore: data.productNameStore || null,
                            // Per-store pack size — fall back to the product's standardizedSize
                            size: data.size || product.standardizedSize || null,
                            standardizedSize: data.standardizedSize || product.standardizedSize || null,
                            unitType: data.unitType || product.unitType || null,
                        };
                    });
                    
                    return { ...product, prices };
                } catch (error) {
                    console.error(`Error fetching prices for ${product.id}:`, error);
                    return { ...product, prices: [] };
                }
            })
        );

        return productsWithPrices;
    } catch (error) {
        console.error('Error fetching products by category:', error);
        return [];
    }
};

// Get prices for a specific product (for backwards compatibility)
export const getPricesForProduct = async (productId, stores) => {
    try {
        const retailersRef = collection(db, 'products', productId, 'retailers');
        const snapshot = await getDocs(retailersRef);

        const pricesMap = {};
        snapshot.docs.forEach(doc => {
            const data = doc.data();
            pricesMap[data.retailerId] = data.latestPricePence / 100; // Convert to pounds
        });

        // Return prices array matching stores
        return stores.map(store => ({
            storeId: store.id,
            storeName: store.name,
            price: pricesMap[store.id] || 0
        }));
    } catch (error) {
        console.error('Error fetching prices:', error);
        return [];
    }
};

// Get cheapest price for a product
export const getCheapestPrice = (prices) => {
    if (!prices || prices.length === 0) return 0;
    return Math.min(...prices.map(p => p.price));
};

// Compare own brand vs branded products
export const compareOwnBrandProducts = async (category) => {
    try {
        const products = await getProductsByCategory(category);
        return {
            ownBrand: products.filter(p => p.ownBrand),
            branded: products.filter(p => !p.ownBrand)
        };
    } catch (error) {
        console.error('Error comparing products:', error);
        return { ownBrand: [], branded: [] };
    }
};

// Get emoji icon for product category
export const getCategoryIcon = (category) => {
    const icons = {
        dairy: '🥛',
        bakery: '🍞',
        meat: '🥩',
        produce: '🥕',
        pantry: '🥫',
        frozen: '🧊',
        household: '🧴'
    };
    return icons[category?.toLowerCase()] || '🛒';
};

// Get gradient background for product category
export const getCategoryGradient = (category) => {
    const gradients = {
        // legacy names
        dairy:           'linear-gradient(135deg, #e8f4f8 0%, #d4e9f2 100%)',
        bakery:          'linear-gradient(135deg, #fff3e6 0%, #ffe4cc 100%)',
        meat:            'linear-gradient(135deg, #ffe6e6 0%, #ffcccc 100%)',
        produce:         'linear-gradient(135deg, #e6f7e6 0%, #ccefcc 100%)',
        pantry:          'linear-gradient(135deg, #fff9e6 0%, #fff3cc 100%)',
        frozen:          'linear-gradient(135deg, #e6f0ff 0%, #cce0ff 100%)',
        household:       'linear-gradient(135deg, #f3e6ff 0%, #e6ccff 100%)',
        // current category names
        fresh_fridge:    'linear-gradient(135deg, #e8f4f8 0%, #d4e9f2 100%)',
        bread_bakery:    'linear-gradient(135deg, #fff3e6 0%, #ffe4cc 100%)',
        fruit_veg:       'linear-gradient(135deg, #e6f7e6 0%, #ccefcc 100%)',
        tinned_cupboard: 'linear-gradient(135deg, #fff9e6 0%, #fff3cc 100%)',
        snacks:          'linear-gradient(135deg, #fef3e6 0%, #fde8cc 100%)',
        drinks:          'linear-gradient(135deg, #e6f0ff 0%, #cce0ff 100%)',
    };
    return gradients[category?.toLowerCase()] || 'linear-gradient(135deg, var(--cream) 0%, #f0ede5 100%)';
};

// ── Product management ────────────────────────────────────────

// Get all products with their retailers subcollection — used by admin product manager.
// Fetches retailers in parallel so the admin view can show store names, price, and size
// without requiring a second click.
export const getAllProducts = async () => {
    try {
        const productsRef = collection(db, 'products');
        const snapshot = await getDocs(productsRef);
        const products = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));

        const withRetailers = await Promise.all(
            products.map(async (product) => {
                try {
                    const retailersRef = collection(db, 'products', product.id, 'retailers');
                    const retailersSnap = await getDocs(retailersRef);
                    const retailers = retailersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
                    return { ...product, retailers };
                } catch {
                    return { ...product, retailers: [] };
                }
            })
        );

        return withRetailers;
    } catch (error) {
        console.error('Error fetching all products:', error);
        return [];
    }
};

// Delete a product and all its retailer sub-documents.
// Firestore does not cascade-delete subcollections from the client SDK,
// so we must fetch and delete retailers first, then the parent.
export const deleteProduct = async (productId) => {
    try {
        const batch = writeBatch(db);

        // 1. Queue all retailer sub-docs for deletion
        const retailersRef = collection(db, 'products', productId, 'retailers');
        const retailersSnap = await getDocs(retailersRef);
        retailersSnap.docs.forEach(retailerDoc => {
            batch.delete(retailerDoc.ref);
        });

        // 2. Queue the parent product document for deletion
        const productRef = doc(db, 'products', productId);
        batch.delete(productRef);

        // 3. Commit everything atomically
        await batch.commit();

        console.log(`[ChopMyShop] Deleted product ${productId} and ${retailersSnap.size} retailer doc(s)`);
        return { success: true };
    } catch (error) {
        console.error(`Error deleting product ${productId}:`, error);
        return { success: false, error: error.message };
    }
};

// ── Community price reporting ─────────────────────────────────
// All community-submitted price reports go into pendingPriceReviews.
// Only admin approval can update live retailer prices (latestPricePence / latestTs).
//
// submitPriceFlag is kept as the public API name for backwards compatibility
// with FlagPriceSheet, but now writes to pendingPriceReviews instead of priceFlags.
//
// DEPRECATED (kept for historical data only — do not use for new writes):
//   getPriceFlags, updateFlagStatus  →  replaced by admin dashboard pendingPriceReviews queries

export const submitPriceFlag = async ({ productId, productName, storeId, retailerDocPath, price, where }) => {
    try {
        const uid = auth.currentUser?.uid ?? null;
        const proposedPricePence = price != null ? Math.round(parseFloat(price) * 100) : null;

        // Snapshot the current live price so the admin dashboard can show a diff
        let currentStoredPricePence = null;
        if (retailerDocPath) {
            try {
                const retailerSnap = await getDoc(doc(db, retailerDocPath));
                if (retailerSnap.exists()) currentStoredPricePence = retailerSnap.data().latestPricePence ?? null;
            } catch (_) { /* non-blocking — dashboard will show — if missing */ }
        }

        const reviewRef = collection(db, 'pendingPriceReviews');
        const docRef = await addDoc(reviewRef, {
            // ── What product / store ─────────────────────────────
            productId,
            productName:    productName ?? null,
            storeId,
            currentStoredPricePence,
            // retailerDocPath is the exact Firestore path to the retailer doc,
            // e.g. "products/milk_whole_2l/retailers/store_tesco".
            // May be null if the caller could not resolve it — admin must verify manually.
            retailerDocPath: retailerDocPath ?? null,

            // ── Proposed price ───────────────────────────────────
            // null means the user flagged an issue without providing a value.
            // Admin dashboard must handle this as "manual verification needed".
            proposedPricePence,
            proposedPromoType:      null,
            proposedPromoPricePence: null,
            proposedPromoEndDate:   null,
            hasPromo:               false,

            // ── Source metadata ──────────────────────────────────
            sourceType:       'community_manual',
            submittedByUid:   uid,
            submissionMethod: where || 'manual',   // 'store' | 'online' | 'manual'
            confidence:       proposedPricePence != null ? 'medium' : 'low',

            // ── Review state ─────────────────────────────────────
            status:           'pending_review',
            fetchedAt:        serverTimestamp(),
            reviewedBy:       null,
            reviewedAt:       null,
            aiRecommendation: null,
            aiReasoning:      null,
        });

        console.log('[ChopMyShop] Community price report submitted:', docRef.id);
        return { success: true, id: docRef.id };
    } catch (error) {
        console.error('[ChopMyShop] Error submitting community price report:', error);
        return { success: false, error: error.message };
    }
};

// DEPRECATED — reads historical priceFlags collection only.
// New pending reviews are in pendingPriceReviews (handled by admin dashboard).
export const getPriceFlags = async () => {
    try {
        const flagsRef = collection(db, 'priceFlags');
        const q = query(flagsRef, orderBy('createdAt', 'desc'));
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
        console.error('Error fetching price flags:', error);
        return [];
    }
};

// DEPRECATED — updates historical priceFlags only.
export const updateFlagStatus = async (flagId, status) => {
    try {
        const flagRef = doc(db, 'priceFlags', flagId);
        await updateDoc(flagRef, {
            status,
            actionedAt: status === 'actioned' ? new Date().toISOString() : null,
        });
        return { success: true };
    } catch (error) {
        console.error('Error updating flag status:', error);
        return { success: false };
    }
};

// Export utilities
export { penceToPounds, poundsToPence };

export { db };
// ── Price review admin functions ──────────────────────────────

// ISO week string e.g. "2026-W12"
function getCurrentIsoWeek() {
    const now    = new Date();
    const jan1   = new Date(now.getFullYear(), 0, 1);
    const week   = Math.ceil(((now - jan1) / 86400000 + jan1.getDay() + 1) / 7);
    return `${now.getFullYear()}-W${String(week).padStart(2, '0')}`;
}

// Load all pending price reviews ordered newest first
export const getPendingPriceReviews = async () => {
    try {
        const q = query(
            collection(db, 'pendingPriceReviews'),
            orderBy('fetchedAt', 'desc'),
        );
        const snap = await getDocs(q);
        return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (error) {
        console.error('[ChopMyShop] Error loading pending reviews:', error);
        return [];
    }
};

// Approve a pending review:
//   1. Mark review approved
//   2. Append to priceReports audit trail
//   3. Update live retailer doc (only when proposedPricePence is not null)
export const approvePriceReview = async (reviewId, review, adminUid) => {
    try {
        const isSystem = review.sourceType === 'system_fetch';

        // 1. Update review status
        await updateDoc(doc(db, 'pendingPriceReviews', reviewId), {
            status:     'approved',
            reviewedBy: adminUid,
            reviewedAt: serverTimestamp(),
        });

        // 2. Audit trail — always write regardless of price presence
        await addDoc(collection(db, 'priceReports'), {
            productId:               review.productId,
            productName:             review.productName   ?? null,
            storeId:                 review.storeId,
            retailerDocPath:         review.retailerDocPath ?? null,
            pricePence:              review.proposedPricePence ?? null,
            proposedPromoType:       review.proposedPromoType ?? null,
            proposedPromoPricePence: review.proposedPromoPricePence ?? null,
            proposedPromoEndDate:    review.proposedPromoEndDate ?? null,
            sourceType:              review.sourceType,
            weekOf:                  getCurrentIsoWeek(),
            approvedBy:              adminUid,
            approvedAt:              serverTimestamp(),
            // System-source fields
            actorId:       isSystem ? 'system_fetcher'           : null,
            parserVersion: isSystem ? (review.parserVersion ?? null) : null,
            fetchRunId:    isSystem ? (review.fetchRunId    ?? null) : null,
            // User-source fields
            userId:         isSystem ? null : (review.submittedByUid ?? null),
            userTrustScore: isSystem ? null : (review.userTrustScore  ?? null),
            userTrustTier:  isSystem ? null : (review.userTrustTier   ?? null),
        });

        // 3. Update live retailer doc — only when we have both a path and a price
        if (review.retailerDocPath && review.proposedPricePence != null) {
            const retailerUpdate = {
                latestPricePence: review.proposedPricePence,
                latestTs:         serverTimestamp(),
                lastVerifiedAt:   serverTimestamp(),
            };
            if (review.proposedPromoPricePence != null) retailerUpdate.promoPricePence = review.proposedPromoPricePence;
            if (review.proposedPromoType)               retailerUpdate.promoType       = review.proposedPromoType;
            if (review.proposedPromoEndDate)            retailerUpdate.promoEndDate    = review.proposedPromoEndDate;
            await updateDoc(doc(db, review.retailerDocPath), retailerUpdate);
        }

        return { success: true };
    } catch (error) {
        console.error('[ChopMyShop] Error approving review:', error);
        return { success: false, error: error.message };
    }
};

// Reject a pending review — status update only, no retailer write
export const rejectPriceReview = async (reviewId, adminUid) => {
    try {
        await updateDoc(doc(db, 'pendingPriceReviews', reviewId), {
            status:     'rejected',
            reviewedBy: adminUid,
            reviewedAt: serverTimestamp(),
        });
        return { success: true };
    } catch (error) {
        console.error('[ChopMyShop] Error rejecting review:', error);
        return { success: false, error: error.message };
    }
};

// Batch approve: eligible = pending + not isPromo + price change within threshold
export const batchApprovePriceReviews = async (reviews, threshold, adminUid) => {
    const eligible = reviews.filter(r =>
        r.status === 'pending_review' &&
        !r.isPromo &&
        r.proposedPricePence != null &&
        r.currentStoredPricePence != null &&
        Math.abs(r.proposedPricePence - r.currentStoredPricePence) / r.currentStoredPricePence <= threshold
    );
    const results = await Promise.all(
        eligible.map(r => approvePriceReview(r.id, r, adminUid))
    );
    const succeeded = results.filter(r => r.success).length;
    return { eligible: eligible.length, succeeded };
};

// Fetch the most recent fetch run summary
export const getLatestFetchRun = async () => {
    try {
        const q = query(collection(db, 'fetchRuns'), orderBy('startedAt', 'desc'), limit(1));
        const snap = await getDocs(q);
        if (snap.empty) return null;
        return { id: snap.docs[0].id, ...snap.docs[0].data() };
    } catch (error) {
        return null;
    }
};

// Read price accuracy system config
export const getSystemConfig = async () => {
    try {
        const snap = await getDoc(doc(db, 'systemConfig', 'priceAccuracy'));
        return snap.exists() ? snap.data() : { batchApproveThreshold: 0.15 };
    } catch (error) {
        return { batchApproveThreshold: 0.15 };
    }
};

// ── Receipt upload ────────────────────────────────────────────────────────────
// Uploads a receipt image to Storage and creates a placeholder Firestore doc.
// Returns { receiptId, unsubscribe } where unsubscribe cancels the status listener.
//
// onProgress(pct: 0-100)   — called during upload
// onStatusChange(data)     — called when Firestore receipt doc changes
//   data fields: { status, itemCount, matchedCount, reviewsCreated, failureReason }
//   status values: 'processing' | 'complete' | 'failed'

export const uploadReceipt = (file, { onProgress, onStatusChange }) => {
    const uid = auth.currentUser?.uid;
    if (!uid) throw new Error('Must be signed in to upload a receipt.');

    // Generate a receipt ID client-side so we know the Firestore doc path immediately
    const receiptId  = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
    const ext        = file.name.split('.').pop()?.toLowerCase() || 'jpg';
    const storagePath = `receipts/${uid}/${receiptId}.${ext}`;
    const storageRef  = ref(storage, storagePath);

    // Start the upload
    const uploadTask = uploadBytesResumable(storageRef, file, {
        contentType: file.type || 'image/jpeg',
        customMetadata: { uid, receiptId },
    });

    // Listen to Firestore doc for processing status
    // The Cloud Function creates this doc once it starts processing.
    let unsubscribe = () => {};
    uploadTask.on(
        'state_changed',
        (snapshot) => {
            const pct = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
            onProgress?.(pct);
        },
        (err) => {
            onStatusChange?.({ status: 'failed', failureReason: err.message });
        },
        () => {
            // Upload complete — start listening to Firestore for OCR results
            const receiptDocRef = doc(db, 'receipts', receiptId);
            unsubscribe = onSnapshot(receiptDocRef, (snap) => {
                if (!snap.exists()) return; // CF hasn't written it yet
                onStatusChange?.(snap.data(), receiptId);
            }, (err) => {
                onStatusChange?.({ status: 'failed', failureReason: err.message });
            });
        }
    );

    return {
        receiptId,
        cancel: () => {
            uploadTask.cancel();
            unsubscribe();
        },
        stopListening: () => unsubscribe(),
    };
};

// ── Save receipt as a persistent shopping list ────────────────────────────────
//
// Aggregates matched receipt items by productId (handles duplicates like
// "BANANA × 3"), then writes a shoppingLists doc owned by the current user.
//
// Returns the new list's Firestore document ID.
export const saveReceiptAsShoppingList = async ({ receiptId, matchedItems, detectedStoreId }) => {
    const uid = auth.currentUser?.uid;
    if (!uid) throw new Error('Must be signed in to save a list.');

    // Aggregate quantities: same productId on multiple lines → { productId, quantity }
    const qtys = {};
    for (const item of matchedItems) {
        if (!item.matchedProductId) continue;
        qtys[item.matchedProductId] = (qtys[item.matchedProductId] || 0) + 1;
    }
    const items = Object.entries(qtys).map(([productId, quantity]) => ({ productId, quantity }));
    if (items.length === 0) throw new Error('No matched items to save.');

    // Auto-generate list name: "Tesco shop – 15 Mar"
    const storeLabel = detectedStoreId
        ? detectedStoreId.replace(/^store_/, '').replace(/\b\w/g, c => c.toUpperCase())
        : 'Receipt';
    const dateLabel = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    const name = `${storeLabel} shop – ${dateLabel}`;

    const docRef = await addDoc(collection(db, 'shoppingLists'), {
        uid,
        name,
        source:         'receipt',
        receiptId:      receiptId || null,          // kept for backwards compatibility
        sourceReceipts: receiptId ? [receiptId] : [], // many-to-many ready
        storeId:        detectedStoreId || null,
        items,
        createdAt: serverTimestamp(),
    });

    // Track reuse on the receipt doc (non-blocking)
    if (receiptId) {
        try {
            await updateDoc(doc(db, 'receipts', receiptId), {
                listCount:         increment(1),
                lastUsedForListAt: serverTimestamp(),
            });
        } catch { /* non-blocking — receipt doc may not exist yet */ }
    }

    return docRef.id;
};

// ── Remove a receipt from Past Scans ─────────────────────────────────────────
// Sets saved: false — keeps the doc for the data engine, removes it from the
// user-visible Past Scans list. Does NOT hard-delete.
export const unsaveReceipt = async (receiptId) => {
    try {
        await updateDoc(doc(db, 'receipts', receiptId), { saved: false });
        return { success: true };
    } catch (error) {
        console.error('[ChopMyShop] Error unsaving receipt:', error);
        return { success: false };
    }
};

// ── Save a receipt to Past Scans ──────────────────────────────────────────────
// Sets saved: true — adds this scan to the user's kept receipt history.
export const saveReceiptToHistory = async (receiptId) => {
    try {
        await updateDoc(doc(db, 'receipts', receiptId), { saved: true });
        return { success: true };
    } catch (error) {
        console.error('[ChopMyShop] Error saving receipt to history:', error);
        return { success: false };
    }
};

// ── Count receipts saved to Past Scans for the current user ──────────────────
// Counts only saved: true docs (old docs without the field are treated as saved
// for backwards compatibility — saved !== false).
export const getReceiptCount = async () => {
    const uid = auth.currentUser?.uid;
    if (!uid) return 0;
    try {
        const q = query(
            collection(db, 'receipts'),
            where('uid', '==', uid),
            limit(15),
        );
        const snap = await getDocs(q);
        return snap.docs.filter(d => d.data().saved !== false).length;
    } catch {
        return 0;
    }
};

// ── Load user's Past Scans (saved receipt docs) ───────────────────────────────
//
// Returns only receipts the user has explicitly saved (saved: true, or old docs
// without the field — backwards compat). Fetches extra docs to account for
// unsaved ones and filters client-side (avoids needing a composite index).
export const getReceiptHistory = async (limitCount = 20) => {
    const uid = auth.currentUser?.uid;
    if (!uid) return [];
    try {
        const fetchLimit = Math.max(limitCount * 5, 60);
        const q = query(
            collection(db, 'receipts'),
            where('uid', '==', uid),
            orderBy('processedAt', 'desc'),
            limit(fetchLimit),
        );
        const snap = await getDocs(q);
        return snap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .filter(r => r.saved !== false)   // saved: true OR old docs without field
            .slice(0, limitCount);
    } catch (error) {
        console.error('[ChopMyShop] Error loading receipt history:', error);
        return [];
    }
};

// ── Load user's saved shopping lists from Firestore ──────────────────────────
//
// Fetches all shoppingLists owned by the current user, then enriches each
// item with product name, size, and prices so BasketPage can render them
// identically to locally-saved baskets.
export const loadUserShoppingLists = async () => {
    const uid = auth.currentUser?.uid;
    if (!uid) return [];

    try {
        // No orderBy here — composite index may still be building.
        // We sort client-side on the Timestamp value instead.
        const q = query(
            collection(db, 'shoppingLists'),
            where('uid', '==', uid),
            limit(50),
        );
        const snap = await getDocs(q);
        const lists = snap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .sort((a, b) => {
                const aMs = a.createdAt?.toMillis?.() ?? 0;
                const bMs = b.createdAt?.toMillis?.() ?? 0;
                return bMs - aMs; // newest first
            });

        // Enrich items: resolve productId → { id, name, size, prices, quantity }
        const enriched = await Promise.all(lists.map(async (list) => {
            const enrichedItems = await Promise.all(
                (list.items || []).map(async ({ productId, quantity }) => {
                    try {
                        const productSnap = await getDoc(doc(db, 'products', productId));
                        if (!productSnap.exists()) return null;
                        const product = { id: productId, ...productSnap.data() };

                        const retailersSnap = await getDocs(
                            collection(db, 'products', productId, 'retailers')
                        );
                        const prices = retailersSnap.docs.map(d => {
                            const data = d.data();
                            return {
                                id:      d.id,
                                storeId: data.retailerId,
                                price:   data.latestPricePence / 100,
                                size:    data.size || product.standardizedSize || null,
                            };
                        });

                        const sizeLabel = product.standardizedSize
                            ? `${product.standardizedSize}${product.unitType ? ' ' + product.unitType : ''}`
                            : null;

                        return { id: productId, name: product.name, size: sizeLabel, quantity, prices };
                    } catch {
                        return null;
                    }
                })
            );

            const createdAt = list.createdAt?.toDate?.()?.toISOString() || new Date().toISOString();
            return {
                id:            list.id,
                name:          list.name,
                source:        list.source || 'receipt',
                items:         enrichedItems.filter(Boolean),
                savedAt:       createdAt,
                lastUsedAt:    createdAt,
                fromFirestore: true,   // flag: delete via Firestore, not localStorage
            };
        }));

        return enriched;
    } catch (error) {
        console.error('[ChopMyShop] Error loading shopping lists:', error);
        return [];
    }
};

// Delete a user-owned shopping list from Firestore
export const deleteShoppingList = async (listId) => {
    try {
        await deleteDoc(doc(db, 'shoppingLists', listId));
        return { success: true };
    } catch (error) {
        console.error('[ChopMyShop] Error deleting shopping list:', error);
        return { success: false };
    }
};

// ── Beta feedback ─────────────────────────────────────────────────────────────
// Writes a feedback response to the `feedback` collection.
// type: 'scan_quality' | 'list_clarity' | 'return_intent' | 'beta_stepper'
export const saveBetaFeedback = async (type, responses) => {
    const uid = auth.currentUser?.uid ?? null;
    try {
        await addDoc(collection(db, 'feedback'), {
            uid,
            type,
            betaSource: true,
            responses,
            status: 'new',
            createdAt: serverTimestamp(),
        });
        return { success: true };
    } catch (error) {
        console.error('[ChopMyShop] Error saving beta feedback:', error);
        return { success: false };
    }
};

// ── Presence ──────────────────────────────────────────────────────────────────
// Writes a heartbeat to presence/{uid} so the admin panel can show
// "active in last 5 minutes". Called on app open and every 60 seconds.

export const updatePresence = async (uid, page, isAnonymous) => {
  if (!uid) return;
  try {
    await setDoc(doc(db, 'presence', uid), {
      lastActiveAt: serverTimestamp(),
      page:         page        ?? 'home',
      isAnonymous:  isAnonymous ?? true,
      appVersion:   '0.1.0',
      updatedAt:    serverTimestamp(),
    }, { merge: true });
  } catch (e) {
    // Presence is best-effort — never block the UI
  }
};

// Returns all presence docs updated in the last `minutesAgo` minutes.
export const getActiveUsers = async (minutesAgo = 5) => {
  const cutoff = Timestamp.fromDate(new Date(Date.now() - minutesAgo * 60 * 1000));
  const snap   = await getDocs(
    query(collection(db, 'presence'), where('lastActiveAt', '>=', cutoff))
  );
  return snap.docs.map(d => ({ uid: d.id, ...d.data() }));
};

// ── Alias management ──────────────────────────────────────────────────────────

const ADMIN_EMAIL = 'hello.chopmyshop@gmail.com';

export function isAdminUser(user) {
  return user?.email === ADMIN_EMAIL;
}

// Normalise receipt text to match productMatcher.js logic
const ALIAS_UNIT_RE = /\b\d+(\.\d+)?\s*(ml|l|g|kg|oz|lb|lbs|cl|cc|mm|cm|pk|pcs?|ct)\b/gi;
function normaliseAlias(str) {
  return (str || '')
    .toLowerCase()
    .replace(ALIAS_UNIT_RE, ' ')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// Save a direct alias (admin use — goes straight to productAliases)
// source: 'user_correction' | 'human_verified' | 'manual' (default: 'user_correction')
export async function saveAliasDirect(db, { receiptText, productId, productName, storeId = '', uid, originalProductId = null, source = 'user_correction' }) {
  const normAlias = normaliseAlias(receiptText);
  if (!normAlias || !productId) return;

  // Check for existing alias with same text + store to avoid duplicates
  const existing = await collection(db, 'productAliases');
  const existingQuery = query(
    existing,
    where('alias',   '==', normAlias),
    where('storeId', '==', storeId),
    limit(1)
  );
  const existingSnap = await getDocs(existingQuery);

  if (!existingSnap.empty) {
    // Update existing alias to point to the corrected product
    await updateDoc(existingSnap.docs[0].ref, {
      productId,
      productName,
      source,
      confirmedByUid:    uid,
      confirmedAt:       serverTimestamp(),
      originalProductId: originalProductId || null,
    });
  } else {
    await addDoc(collection(db, 'productAliases'), {
      alias:            normAlias,
      productId,
      productName,
      storeId,
      source,
      confirmedByUid:   uid,
      confirmedAt:      serverTimestamp(),
      originalProductId: originalProductId || null,
    });
  }
}

// Fetch all aliases for admin panel
export async function getAliases(db) {
  const snap = await getDocs(
    query(
      collection(db, 'productAliases'),
      orderBy('confirmedAt', 'desc'),
      limit(300)
    )
  );
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// Delete an alias
export async function deleteAlias(db, aliasId) {
  await deleteDoc(doc(db, 'productAliases', aliasId));
}

// Save an ignore alias — receipt text that should be permanently skipped
// Uses productId '__ignore__' as a sentinel value
export async function saveIgnoreAlias(db, { receiptText, storeId = '', uid }) {
  const normAlias = normaliseAlias(receiptText);
  if (!normAlias) return;

  const existing = await getDocs(
    query(collection(db, 'productAliases'),
      where('alias',   '==', normAlias),
      where('storeId', '==', storeId),
      limit(1))
  );
  if (!existing.empty) {
    await updateDoc(existing.docs[0].ref, {
      productId:       '__ignore__',
      productName:     null,
      source:          'ignore',
      confirmedByUid:  uid,
      confirmedAt:     serverTimestamp(),
    });
  } else {
    await addDoc(collection(db, 'productAliases'), {
      alias:           normAlias,
      productId:       '__ignore__',
      productName:     null,
      storeId,
      source:          'ignore',
      confirmedByUid:  uid,
      confirmedAt:     serverTimestamp(),
    });
  }
}

// Save a backlog alias — removed from queue but not permanently ignored.
// Stores occurrence count so the backlog can be sorted by frequency.
export async function saveBacklogAlias(db, { receiptText, storeId = '', uid, count = 1 }) {
  const normAlias = normaliseAlias(receiptText);
  if (!normAlias) return;

  const existing = await getDocs(
    query(collection(db, 'productAliases'),
      where('alias',   '==', normAlias),
      where('storeId', '==', storeId),
      limit(1))
  );
  if (!existing.empty) {
    await updateDoc(existing.docs[0].ref, {
      productId:       '__backlog__',
      productName:     null,
      source:          'backlog',
      count,
      confirmedByUid:  uid,
      confirmedAt:     serverTimestamp(),
    });
  } else {
    await addDoc(collection(db, 'productAliases'), {
      alias:           normAlias,
      productId:       '__backlog__',
      productName:     null,
      storeId,
      source:          'backlog',
      count,
      confirmedByUid:  uid,
      confirmedAt:     serverTimestamp(),
    });
  }
}

// Fetch all backlog aliases, grouped by normalised alias text (across stores).
// Returns array sorted by total occurrence count descending.
export async function getBacklogItems(db) {
  const snap = await getDocs(
    query(collection(db, 'productAliases'), where('source', '==', 'backlog'))
  );
  // Group by normalised alias text so the same product from multiple stores collapses
  const groups = {}; // normAlias → { productText, stores: [{storeId, count, docId}], totalCount }
  snap.forEach(d => {
    const { alias, storeId = '', count = 1 } = d.data();
    if (!alias) return;
    if (!groups[alias]) {
      groups[alias] = { productText: alias, stores: [], totalCount: 0 };
    }
    groups[alias].stores.push({ storeId, count, docId: d.id });
    groups[alias].totalCount += count;
  });
  return Object.values(groups).sort((a, b) => b.totalCount - a.totalCount);
}

// Fetch recent matched items from receipt docs for the Review mode in admin aliases panel.
// Returns items sorted by confidenceScore ascending (lowest confidence = most risky = first).
export async function getRecentMatchedItems(db, limitCount = 20) {
  // Load receipts and existing aliases in parallel
  const [receiptsSnap, aliasesSnap] = await Promise.all([
    getDocs(query(collection(db, 'receipts'), orderBy('processedAt', 'desc'), limit(limitCount))),
    getDocs(collection(db, 'productAliases')),
  ]);

  // Build a Set of already-handled alias keys (normalised text|storeId)
  const handledKeys = new Set();
  aliasesSnap.forEach(d => {
    const { alias, storeId = '' } = d.data();
    if (alias) handledKeys.add(`${alias}|${storeId}`);
  });

  const seen    = new Set();
  const results = [];
  for (const d of receiptsSnap.docs) {
    const data = d.data();
    for (const item of (data.matchedItems || [])) {
      if (!item.productText || !item.matchedProductId) continue;
      const normText = normaliseAlias(item.productText);
      const storeId  = data.detectedStoreId || '';
      const key      = `${normText}|${storeId}`;
      if (seen.has(key)) continue;
      // Skip items already handled via any alias (confirmed, corrected, created, etc.)
      if (handledKeys.has(key)) continue;
      seen.add(key);
      results.push({
        productText:        item.productText,
        matchedProductId:   item.matchedProductId,
        matchedProductName: item.matchedProductName,
        matchType:          item.matchType,
        confidence:         item.confidence,
        confidenceScore:    item.confidenceScore ?? null,
        lowConfidence:      item.lowConfidence || false,
        routedToGpt:        item.routedToGpt  || false,
        storeId:            storeId || null,
        receiptId:          d.id,
      });
    }
  }
  // Lowest confidence first — most likely to be wrong
  results.sort((a, b) => (a.confidenceScore ?? 1) - (b.confidenceScore ?? 1));
  return results.slice(0, 60);
}

// Fetch recent unmatched items from receipt docs for the admin aliases panel
export async function getUnmatchedReceiptItems(db, limitCount = 10) {
  // Load receipts and existing aliases in parallel
  const [snap, aliasesSnap] = await Promise.all([
    getDocs(query(collection(db, 'receipts'), orderBy('processedAt', 'desc'), limit(limitCount))),
    getDocs(collection(db, 'productAliases')),
  ]);

  // Build a Set of already-handled alias keys (normalised text|storeId)
  const handledKeys = new Set();
  aliasesSnap.forEach(d => {
    const { alias, storeId = '' } = d.data();
    if (alias) handledKeys.add(`${alias}|${storeId}`);
  });

  // Group by normalised text+store, tracking count
  const groups = {}; // key → { item, count }
  for (const d of snap.docs) {
    const data = d.data();
    for (const item of (data.unmatchedItems || [])) {
      if (!item.productText) continue;
      const normText = normaliseAlias(item.productText);
      const storeId  = data.detectedStoreId || '';
      const key      = `${normText}|${storeId}`;
      // Skip items already handled via any alias (ignored, matched, created, etc.)
      if (handledKeys.has(key)) continue;
      if (groups[key]) {
        groups[key].count += 1;
      } else {
        groups[key] = {
          count: 1,
          item: {
            productText:    item.productText,
            pricePence:     item.pricePence,
            storeId:        storeId || null,
            receiptId:      d.id,
            gptCleanedText: item.gptCleanedText || null,
          },
        };
      }
    }
  }
  // Most frequent first — highest value to fix
  return Object.values(groups)
    .sort((a, b) => b.count - a.count)
    .map(({ item, count }) => ({ ...item, count }));
}

// ── Home screen: receipt-based list building ──────────────────────────────────

// Returns the most recent valid receipt for a user, or null.
// Valid = processed within 14 days, status complete, matchRate ≥ 0.70,
// at least 1 matched item. Used to decide which home screen state to show.
export async function getLatestValidReceipt(db, uid) {
  if (!uid) return null;
  try {
    const cutoff = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000);
    const snap = await getDocs(
      query(
        collection(db, 'receipts'),
        where('uid', '==', uid),
        orderBy('processedAt', 'desc'),
        limit(10)
      )
    );
    for (const d of snap.docs) {
      const data = d.data();
      const processedAt = data.processedAt?.toDate?.() || null;
      // Sorted descending — once we hit a doc older than cutoff, all following are too
      if (!processedAt || processedAt < cutoff) break;
      if (data.status !== 'complete') continue;
      const matchedItems = data.matchedItems || [];
      if (matchedItems.length === 0) continue;
      // matchRate from pipeline metrics; fall back to matchedCount / itemCount
      const matchRate =
        data.pipeline?.matchRate ??
        (data.itemCount > 0 ? (data.matchedCount || 0) / data.itemCount : 0);
      // Usable if ≥50% match rate OR ≥5 matched items (Phase 1 threshold)
      const isUsable = matchRate >= 0.5 || matchedItems.length >= 5;
      if (!isUsable) continue;
      return {
        receiptId:         d.id,
        matchedItems,
        matchedItemsCount: matchedItems.length,
        detectedStoreId:   data.detectedStoreId || null,
        matchRate,
        processedAt,
      };
    }
    return null;
  } catch (err) {
    console.error('[ChopMyShop] getLatestValidReceipt error:', err);
    return null;
  }
}

// Fetch product docs + current prices for a receipt's matched items.
// Returns basket-ready items — de-duped by productId, quantity tracked.
// Used by "Recreate last shop" (State B) and "Add from receipt" (State D).
export async function createProductDirect(db, { productId, name, category, subcategory, variant, storageType, brand, ownBrand, uid }) {
  await setDoc(doc(db, 'products', productId), {
    name,
    category:     category     || null,
    subcategory:  subcategory  || null,
    variant:      variant      || null,
    storageType:  storageType  || null,
    brand:        brand        || null,
    ownBrand:     ownBrand     ?? false,
    active:       true,
    source:       'admin_manual',
    createdByUid: uid          || null,
    createdAt:    serverTimestamp(),
  });
  return productId;
}

export async function buildBasketItemsFromReceipt(db, matchedItems) {
  // Aggregate quantities (same product on multiple receipt lines → qty N)
  const qtys = {};
  for (const item of (matchedItems || [])) {
    if (!item.matchedProductId) continue;
    qtys[item.matchedProductId] = (qtys[item.matchedProductId] || 0) + 1;
  }

  const results = await Promise.all(
    Object.entries(qtys).map(async ([productId, quantity]) => {
      try {
        const [productSnap, retailersSnap] = await Promise.all([
          getDoc(doc(db, 'products', productId)),
          getDocs(collection(db, 'products', productId, 'retailers')),
        ]);
        if (!productSnap.exists()) return null;
        const product = { id: productId, productId, ...productSnap.data() };
        const prices = retailersSnap.docs
          .map(d => {
            const rData = d.data();
            return {
              storeId: rData.retailerId || d.id,
              price:   rData.latestPricePence ? rData.latestPricePence / 100 : null,
              size:    rData.size || null,
            };
          })
          .filter(p => p.price != null);
        return { ...product, quantity, prices, comparisonPreference: null };
      } catch {
        return null;
      }
    })
  );

  return results.filter(Boolean);
}

// ── Test user management ──────────────────────────────────────────────────────
// UIDs stored in config/testUsers are excluded from dashboard metrics so
// internal testing doesn't pollute real user data.

const TEST_USERS_DOC = 'config/testUsers';

export const getTestUserIds = async () => {
  try {
    const snap = await getDoc(doc(db, 'config', 'testUsers'));
    return snap.exists() ? (snap.data().uids || []) : [];
  } catch {
    return [];
  }
};

export const toggleTestUser = async (uid) => {
  const ref  = doc(db, 'config', 'testUsers');
  const snap = await getDoc(ref);
  const current = snap.exists() ? (snap.data().uids || []) : [];
  const updated  = current.includes(uid)
    ? current.filter(u => u !== uid)
    : [...current, uid];
  await setDoc(ref, { uids: updated }, { merge: true });
  return updated;
};

// ── Comparison viewed logging ─────────────────────────────────────────────────
// Written to comparisonViews/{id} when a user opens the store compare sheet.
// Used by the admin dashboard to calculate meaningful comparison rate.
// Best-effort — never blocks the UI.
export const logComparisonViewed = async (itemCount) => {
  const uid = auth.currentUser?.uid ?? null;
  if (!uid) return;
  try {
    await addDoc(collection(db, 'comparisonViews'), {
      uid,
      itemCount: itemCount ?? 0,
      viewedAt: serverTimestamp(),
    });
  } catch {
    // Best-effort, silent failure
  }
};

// ── Dashboard metrics ─────────────────────────────────────────────────────────
// Queries receipts + comparisonViews for the admin dashboard.
// Returns aggregated stats for the given number of days.
// Test user UIDs are fetched and excluded automatically.
export const getDashboardMetrics = async (days = 7) => {
  const cutoff = Timestamp.fromDate(new Date(Date.now() - days * 24 * 60 * 60 * 1000));

  // Fetch test user IDs to exclude from metrics
  const testUids = await getTestUserIds();
  const isTestUser = uid => testUids.includes(uid);

  // Query receipts in window
  const receiptsSnap = await getDocs(
    query(
      collection(db, 'receipts'),
      where('processedAt', '>=', cutoff),
      orderBy('processedAt', 'desc'),
      limit(500),
    )
  );

  const receipts = receiptsSnap.docs.map(d => ({ id: d.id, ...d.data() }))
    .filter(r => r.status === 'complete')
    .filter(r => !isTestUser(r.uid));

  const receiptsProcessed = receipts.length;
  let totalMatchedItems = 0;
  let totalMatchRate    = 0;
  let meaningfulCount  = 0;
  const distribution   = { '0–2': 0, '3–4': 0, '5–9': 0, '10+': 0 };

  receipts.forEach(r => {
    const matched   = r.matchedCount || 0;
    const total     = r.itemCount    || 0;
    const matchRate = total > 0 ? matched / total : 0;

    totalMatchedItems += matched;
    totalMatchRate    += matchRate;

    if      (matched <= 2) distribution['0–2']++;
    else if (matched <= 4) distribution['3–4']++;
    else if (matched <= 9) distribution['5–9']++;
    else                   distribution['10+']++;

    if (matched >= 5 || matchRate >= 0.5) meaningfulCount++;
  });

  const avgMatchedItems     = receiptsProcessed > 0 ? totalMatchedItems / receiptsProcessed : 0;
  const avgMatchRate        = receiptsProcessed > 0 ? totalMatchRate / receiptsProcessed : 0;
  const meaningfulReceiptRate = receiptsProcessed > 0 ? meaningfulCount / receiptsProcessed : 0;

  // Query comparison views in window
  let comparisonsViewed    = 0;
  let meaningfulComparisons = 0;
  try {
    const viewsSnap = await getDocs(
      query(
        collection(db, 'comparisonViews'),
        where('viewedAt', '>=', cutoff),
        orderBy('viewedAt', 'desc'),
        limit(500),
      )
    );
    const viewDocs = viewsSnap.docs.filter(d => !isTestUser(d.data().uid));
    comparisonsViewed    = viewDocs.length;
    meaningfulComparisons = viewDocs.filter(d => (d.data().itemCount || 0) >= 5).length;
  } catch {
    // comparisonViews collection may not exist yet — treat as zero
  }

  const meaningfulComparisonRate = receiptsProcessed > 0
    ? meaningfulComparisons / receiptsProcessed
    : 0;

  return {
    receiptsProcessed,
    comparisonsViewed,
    avgMatchedItems,
    avgMatchRate,
    meaningfulCount,
    meaningfulReceiptRate,
    meaningfulComparisons,
    meaningfulComparisonRate,
    distribution,
  };
};

export default db;
